import _styled from "styled-components";
const _excluded = ["children", "content", "autoTipOnEllipsis", "singleLine", "lineClamp", "getTargetProps"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useRef, useEffect, useState, useMemo } from 'react';
import { ResizeObserver } from '@juggle/resize-observer';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import { isContentOverflowingElement } from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';
import Tooltip from '../Tooltip';
/**
 * Tooltip component display on hover of message with ellipsis.
 */
const OverflowTooltip = _ref => {
  let {
      children,
      content,
      autoTipOnEllipsis = true,
      singleLine = true,
      lineClamp = 2,
      getTargetProps = () => ({
        style: {
          width: '100%'
        }
      })
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // If the content is not truncated, we don't want to show the tooltip or have the target be focusable
  const nonTruncatedProps = useMemo(() => ({
    show: false,
    getTargetProps: () => _objectSpread({
      tabIndex: -1
    }, getTargetProps())
  }), [getTargetProps]);
  const showProps = isTruncated ? {
    getTargetProps
  } : nonTruncatedProps;
  useEffect(() => {
    let resizeObserverEntries = [];
    const checkIfOverflowing = () => setIsTruncated(isContentOverflowingElement(ref));
    const debouncedCheckIfOverflowing = debounce(checkIfOverflowing, 100);

    // Check if overflowing on initial mount
    checkIfOverflowing();

    // Check if overflowing when target's dimension changes
    const resizeObserver = new ResizeObserver(entries => {
      debouncedCheckIfOverflowing();
      resizeObserverEntries = entries;
    });
    if (ref.current) resizeObserver.observe(ref.current);

    // Check if overflowing when target's text content changes
    const mutationObserver = new MutationObserver(debouncedCheckIfOverflowing);
    mutationObserver.observe(ref.current, {
      subtree: true,
      characterData: true
    });
    return () => {
      resizeObserverEntries.forEach(entry => entry.target.remove());
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
  return /*#__PURE__*/React.createElement(Tooltip, _extends({
    content: content || children,
    placement: Tooltip.PLACEMENTS.TOP
  }, showProps, props), /*#__PURE__*/React.createElement(_StyledDiv, {
    ref: ref,
    $_css: [singleLine && autoTipOnEllipsis && theme.typography.ellipsis, !singleLine && autoTipOnEllipsis && {
      display: '-webkit-box',
      WebkitLineClamp: lineClamp,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      whiteSpace: 'normal'
    }, !singleLine && !autoTipOnEllipsis && {
      display: 'inline',
      whiteSpace: 'normal'
    }]
  }, children));
};
OverflowTooltip.propTypes = _objectSpread(_objectSpread({}, Tooltip.propTypes), {}, {
  /**
   * className to add to the Tooltip component
   */
  className: PropTypes.string,
  /**
   * style to add to the Tooltip component
   */
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /**
   * This would be the content if supplied, otherwise it'll use `children` as the content inside the tooltip.
   */
  content: PropTypes.node,
  /**
   * whether to show ellipsis on overflow
   */
  autoTipOnEllipsis: PropTypes.bool,
  /**
   * whether the content should be limited to a single line
   */
  singleLine: PropTypes.bool,
  /**
   * number of lines we want to show using line clamp
   */
  lineClamp: PropTypes.number,
  /**
   * children node or function
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
});
export default Object.assign(OverflowTooltip, {
  Arrow: Tooltip.Arrow,
  Target: Tooltip.Target,
  Body: Tooltip.Body,
  VARIANTS: Tooltip.VARIANTS,
  PLACEMENTS: Tooltip.PLACEMENTS,
  TRIGGER_EVENTS: Tooltip.TRIGGER_EVENTS,
  TRIGGER_TARGETS: Tooltip.TRIGGER_TARGETS
});
var _StyledDiv = _styled("div").withConfig({
  displayName: "OverflowTooltip___StyledDiv",
  componentId: "sc-1kkrwdi-0"
})(["", ""], p => p.$_css);
//# sourceMappingURL=OverflowTooltip.js.map